import { ReactNode } from 'react';
import Box from '@material-ui/core/Box';
import MuiLink from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import HeadsetMicOutlinedIcon from '@material-ui/icons/HeadsetMicOutlined';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { makeStyles } from '@material-ui/core/styles';

const DOCS_URL = 'https://rafaysystems.atlassian.net/wiki/x/AwC1IwE';
const SUPPORT_EMAIL = 'ops@rafay.co';
const ACCENT = '#4CAF9A';

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(1, 1, 2),
    padding: theme.spacing(1.25),
    borderRadius: 12,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  iconBadge: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: `${ACCENT}22`,
    color: ACCENT,
  },
  title: {
    fontSize: '0.8125rem',
    fontWeight: 700,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0.5),
    borderRadius: 6,
    transition: theme.transitions.create('background-color'),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  rowText: {
    flex: '1 1 auto',
    minWidth: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  chevron: {
    color: theme.palette.text.secondary,
    opacity: 0.6,
  },
}));

type SupportRowProps = {
  icon: ReactNode;
  color: string;
  title: string;
  href: string;
};

function SupportRow({ icon, color, title, href }: SupportRowProps) {
  const classes = useStyles();
  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.row}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Box style={{ color, display: 'flex' }}>{icon}</Box>
      <Typography className={classes.rowText}>{title}</Typography>
      <ChevronRightIcon fontSize="small" className={classes.chevron} />
    </MuiLink>
  );
}

// Compact bottom-of-sidebar panel — real destinations: Documentation opens
// the actual runbook, Report an Issue opens a real mailto to the ops inbox.
export const NeedHelpCard = () => {
  const classes = useStyles();

  return (
    <Paper variant="outlined" className={classes.root}>
      <Box className={classes.headerRow}>
        <Box className={classes.iconBadge}>
          <HeadsetMicOutlinedIcon style={{ fontSize: 14 }} />
        </Box>
        <Typography className={classes.title}>Platform Support</Typography>
      </Box>

      <SupportRow
        icon={<MenuBookIcon fontSize="small" />}
        color="#7c6ff0"
        title="Documentation"
        href={DOCS_URL}
      />
      <SupportRow
        icon={<ReportProblemOutlinedIcon fontSize="small" />}
        color="#f5a623"
        title="Report an Issue"
        href={`mailto:${SUPPORT_EMAIL}`}
      />
    </Paper>
  );
};
